import React, { useState } from "react";
import { Check } from "lucide-react";
import { ReactComponent as Thunder } from "../../../../../assets/icons/svg/thunder.svg";
import { ReactComponent as Clock } from "../../../../../assets/icons/svg/clock.svg";
import { ReactComponent as SelectionIcon } from "../../../../../assets/icons/svg/selection-icon.svg";
import { useToast } from "../../../../ui/Toast_Message";
import { usePlaylist } from "../../../../../context/PlaylistContext";
import CustomButton from "../../../../ui/CommonButton";
import AnnouncementSelector from "../../../../ui/AnnouncementCard";
import NewSelectorCard from "../../../../ui/New_Shedule_card";
import LaterSelectorCard from "../../../../ui/Later_Shedule_Card";
import WeeklySchedule from "../../../../ui/Selaction_Schedule_card";

// Types
interface CreativeWithDuration {
  id: string;
  title: string;
  imageUrl: string;
  fileFormat?: string;
  duration?: number;
  [key: string]: any;
}

type AnnouncementType = "general" | "tag-level";
type ScheduleType = "now" | "later" | "selection";

interface DurationOption {
  value: string;
  label: string;
}

interface NowScheduleData {
  endDate: Date;
  duration: DurationOption | null;
}

interface LaterScheduleData {
  startDate: Date;
  endDate: Date;
  duration: DurationOption | null;
}

interface SelectionScheduleData {
  selectedSlots: Set<string>;
  duration: DurationOption | null;
}

// Enhanced Schedule Data Manager
class ScheduleDataManager {
  private data: Map<ScheduleType, any> = new Map();

  setData(type: ScheduleType, scheduleData: any): void {
    this.data.set(type, scheduleData);
  }

  getData(type: ScheduleType): any {
    return this.data.get(type);
  }

  clearAll(): void {
    this.data.clear();
  }

  hasMultipleScheduleTypes(): boolean {
    const completedTypes: ScheduleType[] = [];
    this.data.forEach((data, type) => {
      if (this.isScheduleDataComplete(type, data)) {
        completedTypes.push(type);
      }
    });
    return completedTypes.length > 1;
  }

  getCompletedScheduleTypesCount(): number {
    let count = 0;
    this.data.forEach((data, type) => {
      if (this.isScheduleDataComplete(type, data)) {
        count++;
      }
    });
    return count;
  }

  getCompletedScheduleTypes(): ScheduleType[] {
    const completedTypes: ScheduleType[] = [];
    this.data.forEach((data, type) => {
      if (this.isScheduleDataComplete(type, data)) {
        completedTypes.push(type);
      }
    });
    return completedTypes;
  }

  clearOtherScheduleTypes(keepType: ScheduleType): void {
    const dataToKeep = this.data.get(keepType);
    this.data.clear();
    if (dataToKeep) {
      this.data.set(keepType, dataToKeep);
    }
  }

  isScheduleDataComplete(type: ScheduleType, data: any): boolean {
    if (!data) return false;

    switch (type) {
      case "now":
        return !!(data.endDate && data.duration?.value);
      case "later":
        return !!(data.startDate && data.endDate && data.duration?.value);
      case "selection":
        return !!(data.selectedSlots?.size > 0 && data.duration?.value);
      default:
        return false;
    }
  }
}

// Utility functions
const formatTotalDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  }
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}`;
};

// Progress Bar Component
const ProgressBar = ({ currentStep }: { currentStep: number }) => {
  return (
    <div className="flex items-center gap-4 mb-6">
      <div className="flex items-center gap-2">
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
            currentStep > 1
              ? "bg-[#0091AF] text-white"
              : currentStep === 1
              ? "bg-black text-white"
              : "border border-gray-300 text-gray-500"
          }`}
        >
          {currentStep > 1 ? <Check className="w-3 h-3" /> : "1"}
        </div>
        <span className="text-sm font-medium text-gray-700">Where</span>
      </div>
      <div className="w-8 h-px bg-gray-300"></div>
      <div className="flex items-center gap-2">
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
            currentStep >= 2
              ? "bg-black text-white"
              : "border border-gray-300 text-gray-500"
          }`}
        >
          2
        </div>
        <span className="text-sm font-medium text-gray-700">When</span>
      </div>
    </div>
  );
};

// Schedule Buttons Component
const ScheduleButtons = ({
  onScheduleSelect,
  selectedSchedule,
  scheduleDataManager,
  onScheduleDataChange,
  onClearOtherSchedules,
}: {
  onScheduleSelect: (type: ScheduleType) => void;
  selectedSchedule: ScheduleType | null;
  scheduleDataManager: ScheduleDataManager;
  onScheduleDataChange: (type: ScheduleType, data: any) => void;
  onClearOtherSchedules: (keepType: ScheduleType) => void;
}) => {
  const getButtonStyle = (type: ScheduleType) => {
    const isActive = selectedSchedule === type;
    const baseBtnStyle =
      "w-full flex items-center justify-center gap-1.5 2xl:gap-2 py-2 2xl:py-3 rounded-[8px] text-xs 2xl:text-sm font-medium text-black transition-all duration-200";
    const hoverStyle = "hover:bg-[#F2FDFF] hover:border-[#0BA6C7]";
    const dynamicBorderColor = isActive
      ? "border-[#0BA6C7]"
      : "border-[#1F1F1F]";
    const activeBgStyle = isActive ? "bg-[#F2FDFF]" : "bg-white";

    return `${baseBtnStyle} border-[0.5px] ${dynamicBorderColor} ${hoverStyle} ${activeBgStyle}`;
  };

  const completedTypes = scheduleDataManager.getCompletedScheduleTypes();
  const hasMultipleCompleted = completedTypes.length > 1;

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold text-black mt-1 mb-1">Schedule:</h3>

      <div className="flex gap-3">
        <div className="flex-1">
          <button
            onClick={() => onScheduleSelect("now")}
            className={getButtonStyle("now")}
          >
            <Thunder className="w-5 h-5" />
            <span>Now</span>
          </button>
        </div>
        <div className="flex-1">
          <button
            onClick={() => onScheduleSelect("later")}
            className={getButtonStyle("later")}
          >
            <Clock className="w-5 h-5" />
            <span>Later</span>
          </button>
        </div>
        <div className="flex-1">
          <button
            onClick={() => onScheduleSelect("selection")}
            className={getButtonStyle("selection")}
          >
            <SelectionIcon className="w-5 h-5" />
            <span>Selection</span>
          </button>
        </div>
      </div>

      {/* Show error message when multiple schedule types have data */}
      {hasMultipleCompleted && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex flex-col gap-2">
            <p className="text-sm text-red-600">
              Multiple schedule types are completed: {completedTypes.join(", ")}
            </p>
            <p className="text-xs text-red-500">
              Please choose one schedule type to proceed with.
            </p>
            <div className="flex gap-2 mt-2">
              {completedTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => onClearOtherSchedules(type)}
                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  Keep {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Schedule components */}
      {selectedSchedule === "now" && (
        <NewSelectorCard
          initialData={scheduleDataManager.getData("now")}
          onDataChange={(data: NowScheduleData) =>
            onScheduleDataChange("now", data)
          }
        />
      )}
      {selectedSchedule === "later" && (
        <LaterSelectorCard
          initialData={scheduleDataManager.getData("later")}
          onDataChange={(data: LaterScheduleData) =>
            onScheduleDataChange("later", data)
          }
        />
      )}
      {selectedSchedule === "selection" && (
        <WeeklySchedule
          initialData={scheduleDataManager.getData("selection")}
          onDataChange={(data: SelectionScheduleData) =>
            onScheduleDataChange("selection", data)
          }
        />
      )}
    </div>
  );
};

// Props interface for the main component
interface SchedulePlaylistModalProps {
  playlist: any;
  selectedCreatives: CreativeWithDuration[];
  onClose: () => void;
  onScheduleComplete: () => void;
}

// Main Schedule Playlist Modal Component
const SchedulePlaylistModal: React.FC<SchedulePlaylistModalProps> = ({
  playlist,
  selectedCreatives,
  onClose,
  onScheduleComplete,
}) => {
  const { showToast } = useToast();
  const { schedulePlaylist } = usePlaylist();

  // State for scheduling
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<AnnouncementType | null>(null);
  const [isAnnouncementComplete, setIsAnnouncementComplete] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleType | null>(
    "now"
  );
  const [isScheduleComplete, setIsScheduleComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [scheduleDataManager] = useState(() => new ScheduleDataManager());
  const [announcementDetails, setAnnouncementDetails] = useState<any>(null);

  // Check if schedule data is complete
  const isScheduleDataComplete = (type: ScheduleType, data: any): boolean => {
    return scheduleDataManager.isScheduleDataComplete(type, data);
  };

  // Check if schedule is valid (complete and no conflicts)
  const isScheduleValid = () => {
    if (!selectedSchedule) return false;

    const completedCount = scheduleDataManager.getCompletedScheduleTypesCount();
    if (completedCount > 1) {
      return false;
    }

    const currentData = scheduleDataManager.getData(selectedSchedule);
    const isCurrentComplete = isScheduleDataComplete(
      selectedSchedule,
      currentData
    );

    return isCurrentComplete && completedCount === 1;
  };

  // Handle clearing other schedules
  const handleClearOtherSchedules = (keepType: ScheduleType) => {
    scheduleDataManager.clearOtherScheduleTypes(keepType);
    setSelectedSchedule(keepType);

    const keptData = scheduleDataManager.getData(keepType);
    const isComplete = isScheduleDataComplete(keepType, keptData);
    setIsScheduleComplete(isComplete);
  };

  // Navigation handlers
  const handleNext = () => {
    if (isAnnouncementComplete && currentStep === 1) {
      setCurrentStep(2);
      if (!selectedSchedule) {
        setSelectedSchedule("now");
      }
      setIsScheduleComplete(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
      setIsScheduleComplete(false);
    }
  };

  // Handle announcement selection
  const handleAnnouncementSelect = (
    type: AnnouncementType | null,
    isComplete: boolean = false,
    details?: any
  ) => {
    setSelectedAnnouncement(type);
    setIsAnnouncementComplete(isComplete);
    setAnnouncementDetails(details);
  };

  // Handle schedule selection
  const handleScheduleSelect = (type: ScheduleType) => {
    setSelectedSchedule(type);
    setIsScheduleComplete(false);

    const currentData = scheduleDataManager.getData(type);
    if (currentData && isScheduleDataComplete(type, currentData)) {
      setIsScheduleComplete(true);
    }
  };

  // Handle schedule data changes
  const handleScheduleDataChange = (type: ScheduleType, data: any) => {
    scheduleDataManager.setData(type, data);

    if (type === selectedSchedule) {
      const isComplete = isScheduleDataComplete(type, data);
      setIsScheduleComplete(isComplete);
    }
  };

  // Enhanced generate date range function
  const generateDateRange = (
    scheduleType: ScheduleType,
    scheduleDetails: any
  ): string => {
    const formatDate = (date: Date) => {
      return date.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    switch (scheduleType) {
      case "now":
        return `${formatDate(new Date())} - ${formatDate(
          scheduleDetails.endDate
        )}`;
      case "later":
        return `${formatDate(scheduleDetails.startDate)} - ${formatDate(
          scheduleDetails.endDate
        )}`;
      case "selection":
        return "Custom Schedule";
      default:
        return "Not scheduled";
    }
  };

  // Generate locations string from selected tags and creatives
  const generateLocationsString = (): string => {
    // Extract all unique locations from creatives
    const creativesLocations = selectedCreatives
      .map(creative => creative.locations || "")
      .filter(location => location.trim() !== "");

    const uniqueLocations = Array.from(new Set(creativesLocations));

    if (uniqueLocations.length === 0) {
      return "No locations specified";
    }

    if (uniqueLocations.length <= 3) {
      return uniqueLocations.join(", ");
    }

    return `${uniqueLocations.slice(0, 2).join(", ")}, +${uniqueLocations.length - 2} more`;
  };

  // Handle scheduling - Enhanced with proper data storage and debugging
  const handleSchedule = () => {
    console.log("=== HANDLE SCHEDULE DEBUG ===");
    console.log("selectedAnnouncement:", selectedAnnouncement);
    console.log("selectedSchedule:", selectedSchedule);
    console.log("announcementDetails:", announcementDetails);

    if (!selectedAnnouncement || !selectedSchedule) {
      showToast(
        "error",
        "Incomplete",
        "Please complete all required fields before scheduling."
      );
      return;
    }

    const completedTypes = scheduleDataManager.getCompletedScheduleTypes();
    console.log("completedTypes:", completedTypes);
    
    if (completedTypes.length === 0) {
      showToast(
        "error",
        "Incomplete",
        "Please complete the schedule configuration."
      );
      return;
    }

    if (completedTypes.length > 1) {
      showToast(
        "error",
        "Multiple schedules",
        `Please select one schedule type. Completed: ${completedTypes.join(
          ", "
        )}`
      );
      return;
    }

    const currentScheduleDetails = scheduleDataManager.getData(selectedSchedule);
    console.log("currentScheduleDetails from manager:", currentScheduleDetails);
    
    if (!currentScheduleDetails || !isScheduleValid()) {
      showToast(
        "error",
        "Invalid schedule",
        "Please complete the schedule configuration."
      );
      return;
    }

    try {
      // Prepare comprehensive schedule details for storage
      const scheduleDetailsToStore = {
        scheduleType: selectedSchedule,
        announcement: selectedAnnouncement,
        scheduleData: {
          selectedSlots: currentScheduleDetails.selectedSlots ? 
            Array.from(currentScheduleDetails.selectedSlots) : 
            currentScheduleDetails.selectedSlots,
          duration: currentScheduleDetails.duration,
          startDate: currentScheduleDetails.startDate,
          endDate: currentScheduleDetails.endDate,
        },
        announcementDetails: {
          ...announcementDetails,
          selectedTags: announcementDetails?.selectedTags || [],
          selectedLocations: generateLocationsString().split(", ").filter(loc => loc.trim() !== ""),
          totalCreatives: selectedCreatives.length,
          creativeTitles: selectedCreatives.map(c => c.title),
        },
        scheduledAt: new Date().toISOString(),
        dateRange: generateDateRange(selectedSchedule, currentScheduleDetails),
        locations: generateLocationsString(),
      };

      console.log("=== SCHEDULE DETAILS TO STORE ===");
      console.log("scheduleDetailsToStore:", scheduleDetailsToStore);

      // Save to playlist context with debug logging
      console.log("Calling schedulePlaylist with:", playlist.id, scheduleDetailsToStore);
      schedulePlaylist(playlist.id, scheduleDetailsToStore);

      console.log("=== SCHEDULING COMPLETE ===");
      console.log("Playlist ID:", playlist.id);
      console.log("Schedule details stored:", scheduleDetailsToStore);

      showToast(
        "success",
        "Playlist scheduled!",
        `"${playlist.title}" has been scheduled successfully and is now playing with ${selectedCreatives.length} creatives.`,
        `playlist-scheduled-${playlist.id}-${Date.now()}`
      );

      onScheduleComplete();
    } catch (error) {
      console.error("Failed to schedule playlist:", error);
      showToast(
        "error",
        "Scheduling failed",
        "Failed to schedule playlist. Please try again."
      );
    }
  };

  const totalDuration = selectedCreatives.reduce(
    (sum, creative) => sum + (creative.duration || 10),
    0
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <ProgressBar currentStep={currentStep} />

        {currentStep === 1 && (
          <AnnouncementSelector
            onSelectionChange={handleAnnouncementSelect}
            selectedType={selectedAnnouncement}
          />
        )}

        {currentStep === 2 && (
          <ScheduleButtons
            onScheduleSelect={handleScheduleSelect}
            selectedSchedule={selectedSchedule}
            scheduleDataManager={scheduleDataManager}
            onScheduleDataChange={handleScheduleDataChange}
            onClearOtherSchedules={handleClearOtherSchedules}
          />
        )}
      </div>

      <div className="border-t border-gray-200 my-4"></div>

      <div className="flex gap-3 mt-auto max-w-full">
        <CustomButton
          variant="outline"
          onClick={currentStep === 1 ? onClose : handlePrevious}
          className="flex-1 max-w-[calc(50%-6px)]"
        >
          {currentStep === 1 ? "Cancel" : "Previous"}
        </CustomButton>
        <CustomButton
          variant="default"
          isActive={
            currentStep === 1 ? isAnnouncementComplete : isScheduleValid()
          }
          onClick={currentStep === 1 ? handleNext : handleSchedule}
          disabled={
            currentStep === 1 ? !isAnnouncementComplete : !isScheduleValid()
          }
          className="flex-1 max-w-[calc(50%-6px)]"
        >
          {currentStep === 1 ? "Next" : "Schedule"}
        </CustomButton>
      </div>
    </div>
  );
};

export default SchedulePlaylistModal;